import "bootstrap/dist/css/bootstrap.min.css";
import "../styles/esign.css";
import { React, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import CompleteSignature from "./CompletedSignature";
import { languages, useLanguage } from "./Langs";
import Navbar from "./Navbar";
import ModalPopup from "./ModalPopup";
import Document from "./Document";
import TopButton from "./TopButton";
import { getApiUrl } from "./urlHook";
import is_null from "./ObjectCheck";
import Signer from "./SignerLocation";
import swal from "sweetalert";
import axios from "axios";

document.location.hash = "";
// gets back the scroll bar if you enter the page from the browse
// because that page disables the scrollbar
// it seems to "infect" the esign page
document.documentElement.style.overflow = "scroll";

const apiUrl = getApiUrl();

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

const Esign = () => {
  const sid = document.location.pathname
    .split("/")
    .slice(-1)[0]
    .substring(0, 24);

  const navigate = useNavigate();
  const [lang, setLang] = useLanguage();

  const [modalOpener, setModalOpener] = useState({});
  const [fields, setFields] = useState({});
  const [openHelp, setOpenHelp] = useState(false);
  const [post, setPost] = useState({});
  const [pages, setPages] = useState([]);
  const [zoom, setZoom] = useState(100);
  const positionZoom = useRef(0);
  const [geo, setGeo] = useState({});
  const [loading, setLoading] = useState(false);
  const [blur, setblur] = useState(false);

  const submit = async () => {
    await submitDocument(sid, geo, navigate, lang, fields, setLoading);
  };

  const completedSignatures = [];

  // post request to the api to get the document images and data
  useEffect(() => {
    GetEnvelopeData(sid).then((envelopeData) => {
      if (envelopeData === null) {
        navigate("/error", {
          state: {
            enteredCode: sid,
          },
        });
      } else if (envelopeData === "complete") {
        navigate("/completed");
      } else if (envelopeData === "declined") {
        navigate("/declined");
      } else {
        onStart(envelopeData, sid, navigate, setPost, setGeo, setPages, lang);
      }
    });

    // if the user accidently scrolls away from a sign spot, they can click the sign button to bring them back
    const scrollListener = () => {
      document.location.hash = " ";
    };

    document.addEventListener("scroll", scrollListener);

    return () => {
      document.removeEventListener("scroll", scrollListener);
    };
  }, []);

  if (is_null(post)) {
    return (
      <div>
        <img
          src="../Spinner-0.7s-200px (1).svg"
          alt="loading please wait."
          style={{
            position: "absolute",
            left: "50%",
            top: "50%",
            maxWidth: "500px",
            width: "500px",
            transform: "translate(-50%, -50%)",
          }}
        ></img>
      </div>
    );
  }

  // first we start by parsing the data
  // doc contains envelope data, and an array of the images called imgs
  const doc = JSON.parse(JSON.stringify(post.doc));

  // completed index
  let completedI = 0;
  // get the completed signatures and put them on for the current signer
  // get the other signers and display them on the page
  // so that it looks like the page is signed by the other party
  for (const signer of doc.signers.filter(
    (s) => s.state.toUpperCase() === "DONE"
  )) {
    for (const signature of signer.signatures) {
      for (const location of signature.locations) {
        if (signature.type === "signature" || signature.type === "initial") {
          completedSignatures.push(
            <CompleteSignature
              signatureImage={signature.imgData}
              key={completedI++}
              left={location.posX}
              bottom={location.posY}
              page={location.page - 1}
              type={"img"}
            />
          );
        } else if (signature.type === "textbox") {
          completedSignatures.push(
            <CompleteSignature
              key={completedI++}
              left={location.posX}
              bottom={location.posY}
              page={location.page - 1}
              type={"text"}
              text={signature.imgData}
            />
          );
        }
      }
    }
  }

  let imgs = pages;
  let data = [];
  let documents = [];

  // Find the current Signer of the document
  const signer = doc.signers.find((s) => s._id === sid);
  const split = signer.name.split(/\/| /).filter((f) => f != ""); // split on slash or space
  const firstname =
    split.length > 2 ? signer.name : split.length > 0 ? split[0] : signer.name;
  const lastname = split.length === 2 ? split[1] : "";

  // create and parse the data of the document
  for (const signature of signer.signatures) {
    for (const location of signature.locations) {
      const type = signature.type;
      // if no valid key (older contracts) use the type as key
      const key = signature.key || signature.type;
      let text = "";
      let topText = "";
      switch (signature.type) {
        case "signature":
          text = lang.SignatureRequired;
          topText = lang.Sign;
          break;
        case "initial":
          text = lang.InitialRequired;
          topText = lang.Initials;
          break;
        case "textbox":
          text = lang.InputRequired;
          topText = lang.Input;
          break;
        default:
          text = "Default";
          topText = "Your Input Required";
          break;
      }

      const props = {
        id: 0,
        page: location.page - 1,
        type: type,
        text: text,
        topText: topText,
        uniqueKey: key,
        size: type === "signature" ? null : "initialsImage",
        left: location.posX,
        top: location.posY,
        width: location.width,
        height: location.height,
      };

      data.push(props);
    }
  }

  // Sort by page then sort by Y axis
  // Only used for assigning the correct id
  // Doesnt change the position of the signatures
  data.sort((dataA, dataB) => {
    const a = dataA.page;
    const b = dataB.page;
    if (a > b) {
      return 1;
    } else if (a < b) {
      return -1;
    } else {
      const ay = Number(dataA.top);
      const by = Number(dataB.top);
      if (ay > by) {
        return -1;
      } else if (ay < by) {
        return 1;
      } else {
        return 0;
      }
    }
  });

  // now that we sorted the data, assign them id's
  // set the id of the documents
  for (let i = 0; i < data.length; i++) {
    data[i].id = i;
  }

  let modals = [];
  let modalCount = 0;
  // then we start to create all the components and put them in a list to show on the screen
  for (let i = 0; i < imgs.length; i++) {
    let pageData = data.filter((d) => d.page === i);
    let signerLocations = [];

    if (pageData !== null) {
      for (let j = 0; j < pageData.length; j++) {
        const pd = pageData[j];
        const uniqueKey = pd.uniqueKey === null ? pd.type : pd.uniqueKey;

        if (!(pd.id in modalOpener)) {
          // only setup the keys once on load
          modalOpener[pd.id] = false;
        }

        if (!(uniqueKey in fields)) {
          fields[uniqueKey] = {
            image: "",
            clickSpots: {},
            key: uniqueKey,
          };
        }

        if (!(pd.id in fields[uniqueKey].clickSpots)) {
          fields[uniqueKey].clickSpots[pd.id] = false;
        }

        const modal = (
          <ModalPopup
            lang={lang}
            key={modalCount}
            uniqueKey={uniqueKey}
            textLimit={
              {
                textbox: 40,
                signature: 30,
                initial: 7,
              }[pd.type]
            }
            type={pd.type}
            index={pd.id}
            customClass={"popup"}
            canSubmit={canSubmit}
            customMsg={pd.text}
            imageUrl={fields[uniqueKey].image}
            fields={fields}
            submit={submit}
            getNextId={getNextId}
            maxId={99999}
            setImageUrl={(imgUrl) => {
              fields[uniqueKey].image = imgUrl;
              setFields({
                ...fields,
                [uniqueKey]: fields[uniqueKey],
              });
            }}
            openVar={modalOpener[pd.id]}
            close={(idx) => {
              if (is_null(fields[uniqueKey].image)) {
                fields[uniqueKey].clickSpots[idx] = false;
                setFields({
                  ...fields,
                  [uniqueKey]: fields[uniqueKey],
                });
              }
              setblur(false);
              setModalOpener({ ...modalOpener, [pd.id]: false });
            }}
            firstName={firstname}
            lastName={lastname}
          />
        );
        modals.push(modal);
        const signer = (
          <Signer
            key={modalCount}
            uniqueKey={uniqueKey}
            lang={lang}
            submit={submit}
            id={pd.id + 1}
            index={pd.id}
            showModal={pd.type !== "textbox"}
            setImageUrl={(imgUrl) => {
              fields[uniqueKey].image = imgUrl;
              setFields({
                ...fields,
                [uniqueKey]: fields[uniqueKey],
              });
            }}
            width={pd.width}
            height={pd.height}
            open={() => {
              setblur(true);
              setModalOpener({ ...modalOpener, [pd.id]: true });
            }}
            size={pd.size}
            text={pd.text}
            clicked={fields[uniqueKey].clickSpots[pd.id]}
            setClicked={(asd) => {
              fields[uniqueKey].clickSpots[asd] = true;
              setFields({
                ...fields,
                [uniqueKey]: fields[uniqueKey],
              });
            }}
            topText={pd.topText}
            fields={fields}
            getNextId={getNextId}
            maxId={99999}
            imageUrl={fields[uniqueKey].image}
            left={pd.left}
            top={pd.top}
            zoom={zoom}
            canSubmit={canSubmit}
          />
        );

        modalCount += 1;
        signerLocations.push(signer);
      }
    }

    const completedSignaturesForPage = completedSignatures.filter((s) => {
      return s.props.page === i;
    });

    documents.push(
      <Document
        key={i}
        ID={i}
        src={imgs[i]}
        signerLocations={signerLocations}
        completedSignatures={completedSignaturesForPage}
      />
    );
  }

  const is_blurred = blur ? "blurbg" : "";

  return (
    <div className={`${is_blurred}`}>
      {loading ? loadingScreen : null}
      <div className="navmargin">
        <Navbar
          modals={modals}
          lang={[lang, setLang]}
          zoom={zoom}
          setZoom={setZoom}
          setScrollY={(s) => {
            setZoom(zoom);
          }}
          banner={doc.banner}
          color={doc.color}
          helpColor={doc.helpColor}
          setOpenHelp={setOpenHelp}
          openHelp={openHelp}
          sid={sid}
          submit={submit}
          canSubmit={canSubmit}
          getNextId={getNextId}
          fields={fields}
          geo={geo}
        />
        <TopButton />
      </div>
      <div
        className="esign"
        style={{
          width: `${(800 * zoom) / 100}px`,
          height: "100%",
          transformOrigin: `50% ${0}px`,
          //transform: `scale(${zoom}%)`,
          //scale: `${zoom}%`,
        }}
      >
        <link
          rel="stylesheet"
          href="https://use.fontawesome.com/releases/v5.13.0/css/all.css"
          integrity="sha384-Bfad6CLCknfcloXFOyFnlgtENryhrpZCe29RTifKEixXQZ38WheV+i/6YWSzkz3V"
          crossOrigin="anonymous"
        />
        {modals}
        <div
          className="container"
        // style={{
        //   width: `700px`,
        //   transformOrigin: `50% ${0}px`,
        //   transform: `scale(${zoom}%)`,
        // }}
        >
          {documents}
        </div>
      </div>
    </div>
  );
};

const GetEnvelopeData = (signatureId) => {
  return (async () => {
    try {
      let access_res = await axios
        .post(`${apiUrl}/access`, {
          signer_id: signatureId,
        })
        .catch((e) => {
          console.log(e);
        }); // if it fails the first time, retry 60 times because the server sometimes takes a while to respond

      if (access_res && access_res.data.err && access_res.data.isRedirect) {
        // api will check to see if they already signed it, we cant redirect from the api
        // so we will return the redirect page here instead
        return access_res.data.redirect;
      }

      // attempt to load the page, and wait if the images havent loaded yet
      // if it fails to get the images, we go to an error page instead
      const try_count = 60; //boot out if more than 60 seconds of waiting for contract to load
      let count = 0;
      //
      while (access_res.data.imgs.length < 1) {
        await sleep(500);
        access_res = await axios
          .post(`${apiUrl}/access`, {
            signer_id: signatureId,
          })
          .catch((e) => {
            console.log(e);
          });
        count += 1;
        if (count > try_count) {
          //esign.myshs.com:3050/api/checklang
          https: throw new Error(
            "Something went wrong getting the data for contracts. "
          );
        }
      }

      return access_res;
    } catch (ex) {
      return null;
    }
  })();
};

const onStart = (
  envelopeData,
  signatureId,
  navigate,
  setPost,
  setGeo,
  setPages,
  lang
) => {
  // https://ultimatecourses.com/blog/using-async-await-inside-react-use-effect-hook
  // you cant just use async in an use Effect block
  // so I made a function inside that will then be called
  (async () => {
    const envelope = envelopeData.data.doc;
    let imgs = [];
    // the db contains the raw png data so append the type here
    for (const i of envelopeData.data.imgs) {
      imgs.push("data:image/png;base64," + i);
    }
    setPages(imgs);
    // check the signer id, and get their state
    const signer = envelope.signers.find((s) => s._id === signatureId);
    const state = signer.state.toLowerCase();
    const status = envelope.status.toLowerCase();
    const logo = (envelope.logo || "").toLowerCase();
    if (status.includes("declined")) {
      navigate(`/declined/${logo}`);
      return;
    } else if (state.includes("done")) {
      navigate(`/completed/${logo}`);
      return;
    }
    // change the logo and color depending on the envelope data
    switch (logo) {
      case "shs":
        envelope.color = "#0a213c";
        envelope.helpColor = "#fff";
        envelope.banner = 0;
        break;
      case "ssi":
        envelope.color = "#cccccc";
        envelope.helpColor = "#000";
        envelope.banner = 1;
        break;
      case "sga":
        envelope.color = "#cccccc";
        envelope.helpColor = "#000";
        envelope.banner = 2;
        break;
      default:
        envelope.color = "#0a213c";
        envelope.helpColor = "#fff";
        envelope.banner = 0;
        break;
    }
    setPost(envelopeData.data);
    // turn off scrolling until they agree so they cant look at the document ahead of time
    document.documentElement.style.overflow = "hidden";

    // first thing the user sees when the page loads successfully
    // we check the language from the database here
    let _lang = (
      await axios.post("https://esign.myshs.com:3050/api/checklang", {
        signer_id: signatureId,
      })
    ).data;

    await swal({
      title: languages[_lang].AgreeSign,
      icon: "info",
      text: languages[_lang].ClickingAgree,
      closeOnClickOutside: false,
      button: languages[_lang].Agree,
    })
      .then((a) => {
        // only ask for geo once
        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition((pos) => {
            setGeo(pos);
          });
        }
      })
      .catch((e) => {
        console.log(e);
      });
    // gets back the scroll bar if you enter the page from the browse
    // because that page disables the scrollbar
    // it seems to "infect" the esign page
    document.documentElement.style.overflow = "scroll";
    document.body.style.overflow = "auto";
    const agent = navigator.userAgent;
    // we check to make sure the user viewed the page and send to mongo
    await axios
      .post(`${apiUrl}/viewed`, {
        signerId: signatureId,
        agent: agent,
      })
      .catch((e) => {
        console.log(e);
      });
  })();
};

const submitDocument = async (sid, geo, navigate, lang, fields, setLoading) => {
  // We check the fields for missing images or spots not clicked
  for (let key in fields) {
    let field = fields[key];
    if (
      is_null(field.image) &&
      Object.values(field.clickSpots).some((c) => !c) // look over all the click spots to make sure none all false
    ) {
      if (field.key === "signature") {
        swal({
          title: lang.MissingSignature,
          text: lang.LookOver,
          button: lang.Ok,
          icon: "warning",
        });
      } else if (field.key === "initial") {
        swal({
          title: lang.MissingInitial,
          text: lang.LookOver,
          button: lang.Ok,
          icon: "warning",
        });
      } else {
        swal({
          title: lang.MissingLocation,
          text: lang.LookOver,
          button: lang.Ok,
          icon: "warning",
        });
      }
      return;
    }
  }

  setLoading(true);

  let signatureData = {
    sid: sid,
    fields: Object.values(fields).map((field) => {
      return { key: field.key.trim(), image: field.image.trim() };
    }),
    latitude: "",
    longitude: "",
  };

  if (geo.coords) {
    signatureData.latitude = geo.coords.latitude.toString();
    signatureData.longitude = geo.coords.longitude.toString();
  }

  // if not geo, submit
  // cache geo when the page loads so we only have to ask once
  const jsonSignatureData = JSON.stringify(signatureData);

  await axios
    .post(`${apiUrl}/submit`, jsonSignatureData, {
      headers: { "Content-Type": "application/json" },
    })
    .then((res) => {
      setLoading(false);
      navigate(`/completed`);
    })
    .catch(async (e) => {
      setLoading(false);
      await swal({
        title: "Error on submitting the contract",
        text: e,
        icon: "error",
      });
      navigate(`/error`);
    });
};

const loadingScreen = (
  <div
    id="fade-background-black"
    style={{
      position: "fixed",
      margin: "0px",
      left: "50%",
      top: "50%",
      width: "100%",
      height: "100%",
      transform: "translate(-50%, -50%)",
      backgroundColor: "rgba(0, 0, 0, 0.6)",
      zIndex: "989",
    }}
  >
    <img
      id="loading"
      alt="spinner"
      src="../Spinner-0.7s-200px (1).svg"
      style={{
        position: "absolute",
        left: "50%",
        top: "50%",
        width: "70%",
        height: "auto",
        transform: "translate(-50%, -50%)",
        zIndex: "999",
      }}
    ></img>
  </div>
);

const canSubmit = (fields) => {
  // make sure all fields are not empty, clicked, and has an image
  return Object.values(fields).every((f) => {
    return f.image !== "" && Object.values(f.clickSpots).every((c) => c);
  });
};

const getNextId = (fields) => {
  // this finds the next lowest id that hasnt been clicked
  // the keys of clickSpots is the id of the sign location
  // so we return the key as the next id
  console.log(fields);
  const max_id = 99999;
  let next_id = max_id;
  Object.values(fields).sort().forEach((f) => {
    if (Object.values(f.clickSpots).some((c) => !c)) {
      let id = Object.keys(f.clickSpots).find((k) => !f.clickSpots[k]);
      if (!Number.isNaN(id)) {
        id = Number(id)
        if (id < next_id) {
          next_id = id;
        }
      }
    }
  });
  console.log(next_id);
  return next_id;
};

export default Esign;
